/**
 * FileUploadForInference - File upload component for schema inference
 *
 * Handles file upload with drag-and-drop support for schema inference.
 * Supports CSV, Parquet, and JSON file formats.
 */

import React, { useCallback, useState } from 'react';
import { Upload, FileText, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { useInferSchema } from '@/features/schemas/hooks/useSchemas';
import { useSchemaCreationStore } from '@/features/schemas/store/schemaCreationStore';

/**
 * Maximum file size (50MB)
 */
const MAX_FILE_SIZE = 50 * 1024 * 1024;

/**
 * Format file size for display
 */
const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

/**
 * FileUploadForInference Component
 *
 * Provides drag-and-drop file upload interface for schema inference.
 * On successful upload, calls the inference API and stores results.
 *
 * @example
 * ```tsx
 * <FileUploadForInference />
 * ```
 */
export const FileUploadForInference: React.FC = () => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  const inferSchema = useInferSchema();
  const store = useSchemaCreationStore();

  /**
   * Validate file before upload
   */
  const validateFile = useCallback((file: File): string | null => {
    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return `File size exceeds maximum allowed size of ${formatFileSize(MAX_FILE_SIZE)}`;
    }

    // Check file extension
    const extension = `.${file.name.split('.').pop()?.toLowerCase()}`;
    const validExtensions = ['.parquet', '.csv', '.json'];

    if (!validExtensions.includes(extension)) {
      return `Invalid file type. Accepted formats: ${validExtensions.join(', ')}`;
    }

    return null;
  }, []);

  /**
   * Handle file upload and schema inference
   */
  const handleFileUpload = useCallback(
    async (file: File) => {
      setUploadError(null);

      // Validate file
      const validationError = validateFile(file);
      if (validationError) {
        setUploadError(validationError);
        return;
      }

      setUploadedFile(file);
      store.setUploadedFile(file);

      try {
        // Call inference API
        const result = await inferSchema.mutateAsync(file);

        // Map InferredSchemaField to SchemaField for the store
        const schemaFields = result.fields.map((field) => ({
          name: field.name,
          type: field.type as any, // Map string type to FieldType
          nullable: field.nullable,
          description: undefined,
          metadata: {
            confidence: field.confidence,
            null_percentage: field.null_percentage,
            sample_values: field.sample_values,
          },
          isPII: result.detected_pii.includes(field.name),
        }));

        // Store inference results
        store.setInferenceResults({
          fields: schemaFields,
          fileFormat: result.file_format,
          rowCount: result.row_count,
          detectedPII: result.detected_pii,
        });

        // Move to next step
        store.nextStep();
      } catch (error: any) {
        console.error('Schema inference failed:', error);
        setUploadError(
          error.response?.data?.detail ||
            error.message ||
            'Failed to infer schema from file. Please check the file format and try again.'
        );
        setUploadedFile(null);
        store.setUploadedFile(null);
      }
    },
    [inferSchema, store, validateFile]
  );

  /**
   * Handle drag events
   */
  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        handleFileUpload(files[0]);
      }
    },
    [handleFileUpload]
  );

  /**
   * Handle file input change
   */
  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        handleFileUpload(files[0]);
      }
    },
    [handleFileUpload]
  );

  /**
   * Reset upload state
   */
  const handleReset = useCallback(() => {
    setUploadedFile(null);
    setUploadError(null);
    store.setUploadedFile(null);
  }, [store]);

  const isUploading = inferSchema.isPending;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Upload Sample File</CardTitle>
        <CardDescription>
          Upload a sample data file (Parquet, CSV, or JSON) to automatically infer the schema
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Drag and drop zone */}
        <div
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          className={`
            relative border-2 border-dashed rounded-lg p-12 text-center transition-colors
            ${isDragging ? 'border-primary bg-primary/5' : 'border-border'}
            ${isUploading ? 'opacity-50 pointer-events-none' : 'cursor-pointer hover:border-primary hover:bg-primary/5'}
          `}
        >
          <input
            type="file"
            id="file-upload"
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            accept=".parquet,.csv,.json"
            onChange={handleFileInputChange}
            disabled={isUploading}
          />

          <div className="flex flex-col items-center gap-4">
            {isUploading ? (
              <>
                <Loader2 className="w-12 h-12 text-primary animate-spin" />
                <div className="space-y-2">
                  <p className="text-lg font-medium">Analyzing file...</p>
                  <p className="text-sm text-muted-foreground">
                    Inferring schema from your data
                  </p>
                </div>
              </>
            ) : uploadedFile ? (
              <>
                <CheckCircle2 className="w-12 h-12 text-success" />
                <div className="space-y-1">
                  <p className="text-lg font-medium">{uploadedFile.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {formatFileSize(uploadedFile.size)}
                  </p>
                </div>
              </>
            ) : (
              <>
                <Upload className="w-12 h-12 text-muted-foreground" />
                <div className="space-y-1">
                  <p className="text-lg font-medium">
                    Drop your file here, or click to browse
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Supports Parquet, CSV, and JSON files (max {formatFileSize(MAX_FILE_SIZE)})
                  </p>
                </div>
              </>
            )}
          </div>
        </div>

        {/* File info */}
        {uploadedFile && !isUploading && (
          <Alert>
            <FileText className="h-4 w-4" />
            <AlertDescription className="flex items-center justify-between">
              <span>
                <strong>{uploadedFile.name}</strong> - {formatFileSize(uploadedFile.size)}
              </span>
              <Button variant="ghost" size="sm" onClick={handleReset}>
                Remove
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Error message */}
        {uploadError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{uploadError}</AlertDescription>
          </Alert>
        )}

        {/* Upload progress */}
        {isUploading && (
          <div className="space-y-2">
            <Progress value={undefined} className="w-full" />
            <p className="text-sm text-center text-muted-foreground">
              Please wait while we analyze your file...
            </p>
          </div>
        )}

        {/* Instructions */}
        <div className="pt-4 space-y-2 text-sm text-muted-foreground">
          <p className="font-medium">What happens next:</p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>We'll analyze the file structure and data types</li>
            <li>Field types and nullable constraints will be automatically detected</li>
            <li>You'll be able to review and modify the inferred schema</li>
            <li>PII fields will be flagged for your review</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};
